import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Scroll Animate - lightweight scroll-triggered animations using GSAP.
 *
 * Usage in HTML:
 *   data-animate="fade-up"       - fade in + slide up (default)
 *   data-animate="fade-down"     - fade in + slide down
 *   data-animate="fade-left"     - fade in + slide from left
 *   data-animate="fade-right"    - fade in + slide from right
 *   data-animate="zoom-in"       - fade in + scale up
 *   data-animate="fade"          - fade in only
 *
 * Optional attributes:
 *   data-animate-delay="0.2"     - delay in seconds
 *   data-animate-duration="0.8"  - duration in seconds (default: 1)
 *   data-animate-stagger="0.1"   - stagger children with [data-animate] inside a [data-animate-group]
 *
 * Stagger groups:
 *   Add data-animate-group on a parent, and data-animate on each child.
 *   Children animate sequentially with the specified stagger value.
 *
 * Example:
 *   <h1 data-animate="fade-up">Title</h1>
 *   <div data-animate-group data-animate-stagger="0.15">
 *       <div data-animate="fade-up">Card 1</div>
 *       <div data-animate="fade-up">Card 2</div>
 *       <div data-animate="fade-up">Card 3</div>
 *   </div>
 */
const scrollAnimate = {
	defaults: {
		duration: 1,
		ease: 'power2.out',
		start: 'top 85%',
	},

	// Start from 0.1 instead of 0 - fully transparent elements are skipped as
	// LCP candidates, and the difference is not visible during the fade.
	animations: {
		'fade-up': {opacity: 0.1, y: 100},
		'fade-down': {opacity: 0.1, y: -100},
		'fade-left': {opacity: 0.1, x: -100},
		'fade-right': {opacity: 0.1, x: 100},
		'zoom-in': {opacity: 0.1, scale: 0.92},
		'fade': {opacity: 0.1},
	},

	init() {
		gsap.config({force3D: true});
		this.animateSingles();
		this.animateGroups();
	},

	animateSingles() {
		const elements = document.querySelectorAll('[data-animate]:not([data-animate-group] [data-animate])');

		elements.forEach((el) => {
			const type = el.getAttribute('data-animate') || 'fade-up';
			const from = this.animations[type] || this.animations['fade-up'];
			const duration = parseFloat(el.getAttribute('data-animate-duration')) || this.defaults.duration;
			const delay = parseFloat(el.getAttribute('data-animate-delay')) || 0;

			gsap.set(el, {...from, willChange: 'transform, opacity'});

			gsap.to(el, {
				...this.getToValues(from),
				duration: duration,
				delay: delay,
				ease: this.defaults.ease,
				clearProps: 'willChange',
				scrollTrigger: {
					trigger: el,
					start: this.defaults.start,
					once: true,
				},
			});
		});
	},

	animateGroups() {
		const groups = document.querySelectorAll('[data-animate-group]');

		groups.forEach((group) => {
			const children = group.querySelectorAll('[data-animate]');
			if (!children.length) return;

			const stagger = parseFloat(group.getAttribute('data-animate-stagger')) || 0.1;
			const duration = parseFloat(group.getAttribute('data-animate-duration')) || this.defaults.duration;
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: group,
					start: this.defaults.start,
					once: true,
				},
			});

			children.forEach((el, i) => {
				const type = el.getAttribute('data-animate') || 'fade-up';
				const from = this.animations[type] || this.animations['fade-up'];
				gsap.set(el, {...from, willChange: 'transform, opacity'});

				tl.to(el, {
					...this.getToValues(from),
					duration: duration,
					ease: this.defaults.ease,
					clearProps: 'willChange',
				}, i * stagger);
			});
		});
	},

	getToValues(from) {
		const to = {};
		if ('opacity' in from) to.opacity = 1;
		if ('y' in from) to.y = 0;
		if ('x' in from) to.x = 0;
		if ('scale' in from) to.scale = 1;
		return to;
	},
};

export default scrollAnimate;
